import { inject, Injectable } from '@angular/core';
import { isDefined } from '@sama/js-core';
import { isEmpty } from 'lodash';
import { Box3, Ray, Vector3 } from 'three';
import { assertDefined } from '../../cross-team-shared/misc-utils/strict-record';
import {
  Point2D,
  Point3D,
} from '../../team-apis/otterdam/answers/shape-typings';
import { CuboidLocationState3D } from '../state/cuboid-location-state-3d';
import { PointCloudContentStore } from '../state/point-cloud-content-store';
import { buildCuboidPointsFromShapeLocation } from './cuboid-utils';
import {
  findClosestPoints,
  Point,
  SearchConfiguration,
} from './kd-tree/find-closest-points';
import { PointCloudAssetService } from './loading/point-cloud-asset.service';
import {
  buildMatrixWorldFromLocation,
  pointToVector,
  TentPoleVertex,
  vectorToPoint,
} from './point-cloud-utils';
import { subtractPoints } from './point-utils';

@Injectable()
export class KdTreeService {
  private pointCloudAssetService = inject(PointCloudAssetService);
  private store = inject(PointCloudContentStore);

  getGroundIntersection(ray: Ray) {
    const closestPoints = this.findClosestPointsToRay(ray, 100, 'ground');
    const groundZ = this.getPercentile(closestPoints.map((p) => p.position)).z;

    const scaleFactor = (groundZ - ray.origin.z) / ray.direction.z;

    return vectorToPoint(
      new Vector3().addVectors(
        ray.origin,
        ray.direction.multiplyScalar(scaleFactor),
      ),
    );
  }

  getGroundZ(
    xyCoordinate: Point2D,
    searchRadius: number | undefined = undefined,
    pointsToUse: 'ground' | 'cloud' | 'all' = 'ground',
  ) {
    const closestPoints = this.getClosestPoints(
      xyCoordinate,
      searchRadius,
      pointsToUse,
    );
    return this.getPercentile(closestPoints.map((p) => p.position)).z;
  }

  getClosestPoint(ray: Ray) {
    // Assuming at least one ground point is found
    return this.findClosestPointsToRay(ray, 1, 'all').map((p) => p.position)[0];
  }

  getClosestPoints(
    xyCoordinate: Point2D,
    searchRadius: number | undefined = undefined,
    pointsToUse: 'ground' | 'cloud' | 'all' = 'ground',
  ) {
    const ray = new Ray();
    const originZ =
      this.store.state().assetBoundingBox?.max[2] || Number.MAX_SAFE_INTEGER;

    ray.origin.fromArray([...xyCoordinate, originZ]);
    ray.direction.fromArray([0, 0, -1]);

    let result = this.findClosestPointsToRay(
      ray,
      100,
      pointsToUse,
      searchRadius,
    );
    if (isEmpty(result)) {
      result = this.findClosestPointsToRay(ray, 100, pointsToUse);
    }
    return result;
  }

  getPointsWithinLocation(
    location: { position: Point3D; scale: Point3D; rotation: Point3D },
    sortPredicate?: (a: Point, b: Point) => number,
  ) {
    const position = pointToVector(location.position);
    const ray = new Ray();
    ray.origin.set(position.x, position.y, Number.MAX_SAFE_INTEGER);
    ray.direction.set(0, 0, -1);

    const orientedBoxVertices =
      buildCuboidPointsFromShapeLocation(location).map(pointToVector);
    const cloud = this.getCloudAsset(this.store.state().render.frameNumber);
    assertDefined(cloud);

    const result = findClosestPoints(
      {
        cloud,
        ray,
        pointsToUse: 'cloud',
        searchBox: {
          orientedBoxVertices,
          worldToLocalMatrix: buildMatrixWorldFromLocation(location).invert(),
          normalAxes: getCuboidNormalAxes(location),
          boundingBox: new Box3().setFromPoints(orientedBoxVertices),
        },
      },
      sortPredicate,
    );

    return result.length ? result : undefined;
  }

  private findClosestPointsToRay(
    ray: Ray,
    nbPoints = 1,
    pointsToUse: 'ground' | 'cloud' | 'all' = 'ground',
    searchRadius: number | undefined = undefined,
  ) {
    const configuration: SearchConfiguration = {
      cloud: this.getCloudAsset(this.store.state().render.frameNumber)!,
      pointsToUse,
      nbPoints,
      ray,
      searchRadius,
    };

    return findClosestPoints(configuration);
  }

  private getCloudAsset(frameNumber: number) {
    const accumulatedGround =
      this.pointCloudAssetService.getAccumulatedGroundAsset();
    return isDefined(accumulatedGround)
      ? accumulatedGround
      : this.pointCloudAssetService.getAsset(frameNumber);
  }

  private getPercentile(points: Vector3[]) {
    points.sort((a, b) => a.z - b.z);
    if (isDefined(this.pointCloudAssetService.getAccumulatedGroundAsset())) {
      // Use median (50th percentile) instead of 25th percentile
      // because we expect less false positives from accumulated ground
      return points[Math.floor(points.length / 2)];
    }
    // 25th percentile or median
    return points[Math.floor(points.length / 4)];
  }
}

function getCuboidNormalAxes(
  location: Pick<CuboidLocationState3D, 'position' | 'rotation' | 'scale'>,
): [Vector3, Vector3, Vector3] {
  const [frontBottomRight, frontTopRight, frontBottomLeft, backBottomRight] =
    getCuboidNormalAxesVertices(location);

  const frontToBackEdge = pointToVector(
    subtractPoints(backBottomRight, frontBottomRight),
  );
  const rightToLeftEdge = pointToVector(
    subtractPoints(frontBottomLeft, frontBottomRight),
  );
  const bottomToTopEdge = pointToVector(
    subtractPoints(frontTopRight, frontBottomRight),
  );

  // u, v, w
  return [frontToBackEdge, rightToLeftEdge, bottomToTopEdge];
}

function getCuboidNormalAxesVertices(
  location: Pick<CuboidLocationState3D, 'position' | 'rotation' | 'scale'>,
) {
  const cuboidPoints = buildCuboidPointsFromShapeLocation(location);
  const axesVertices: [Point3D, Point3D, Point3D, Point3D] = [
    cuboidPoints[TentPoleVertex.FrontBottomRight],
    cuboidPoints[TentPoleVertex.FrontTopRight],
    cuboidPoints[TentPoleVertex.FrontBottomLeft],
    cuboidPoints[TentPoleVertex.BackBottomRight],
  ];
  return axesVertices;
}
